import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronRight, Repeat } from 'lucide-react';
import {
  availableGoalIds,
  buildComingUp,
  comingUpAction,
  comingUpChip,
  comingUpState,
  goalSlots,
  showsShowAll,
  type ComingUpChip,
  type ComingUpGoalInput,
  type ComingUpItem,
  type ComingUpRead,
  type ComingUpRoutineInput,
  type ComingUpTaskInput,
} from '../../../lib/coming-up';
import { fromCardBackground } from '../../../lib/card-background-click';
import { GOAL_INTENTS, goalIntentContext, type GoalIntent } from '../../../lib/goal-intents';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useAgentName } from '../../../lib/use-agent-name';
import { AgentDot } from '../../AgentDot';
import { Modal } from '../../Modal';
import { MaestroMark } from '../MaestroMark';
import { Badge, Button, Card, ScrollArea, TextButton, Typography, type BadgeVariant } from '../ui';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3ComingUp.module.css';

/** Which goal intents were started from this section. Per browser, like the phones' own prefs key. */
const STARTED_GOALS_KEY = 'symphony-coming-up-started-goals';

/**
 * The hidden line that rides beside "What should I do next for my business?", so the Maestro knows the
 * ask came from an empty Home and does not have to guess it from the words. Byte-identical on iOS and
 * Android.
 */
export const ASK_MAESTRO_CONTEXT =
  'The user tapped Ask Maestro on an empty Home Coming up section: nothing is scheduled for the next 7 days. They want concrete next steps for their business.';

function readStartedGoals(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STARTED_GOALS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeStartedGoals(ids: Set<string>): void {
  try {
    window.localStorage.setItem(STARTED_GOALS_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage off (private window): the goal is simply offered again next time.
  }
}

const CHIP_VARIANT: Record<ComingUpChip['kind'], BadgeVariant> = {
  needsYou: 'waiting-on-you',
  overdue: 'failed',
  today: 'in-progress',
  tomorrow: 'in-progress',
  inDays: 'in-progress',
  routine: 'in-progress',
  suggestedGoal: 'waiting-on-a-reply',
};

/** One slot of the section: a real item, or a goal suggestion filling an empty one. */
type Slot = { kind: 'item'; item: ComingUpItem } | { kind: 'goal'; goal: GoalIntent };

export interface Home3ComingUpProps {
  tasks: readonly ComingUpTaskInput[];
  routines: readonly ComingUpRoutineInput[];
  goals: readonly ComingUpGoalInput[];
  reads: { tasks: ComingUpRead; routines: ComingUpRead; goals: ComingUpRead };
  liveWorkspaceIds: ReadonlySet<string> | null;
  agentNames: Record<string, string>;
  spaceNames: Record<string, string>;
  /** The phone layout: cards in a horizontal row. Otherwise the desktop list beside the brief. */
  narrow: boolean;
  onOpen: (item: ComingUpItem) => void;
  onRetry: () => void;
  /** DEV preview only: the goal intents to treat as started, instead of this browser's record. */
  startedGoalsOverride?: readonly string[];
}

/**
 * The home's "Coming up" section (Figma 21417:120782 desktop, 21417:134808 phone) — three slots of what
 * happens next and what needs the reader, filled with suggested goals when there is room. Every rule
 * about WHICH items, their order and their chip is `lib/coming-up.ts`; this component only draws.
 *
 * The desktop draws the slots as rows beside the brief; the phone as cards in a horizontal row whose
 * next card peeks. "Show all" opens every real item in a modal.
 */
export function Home3ComingUp({
  tasks,
  routines,
  goals,
  reads,
  liveWorkspaceIds,
  agentNames,
  spaceNames,
  narrow,
  onOpen,
  onRetry,
  startedGoalsOverride,
}: Home3ComingUpProps) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const maestroName = useAgentName().short;
  const [showAll, setShowAll] = useState(false);
  const [started, setStarted] = useState<Set<string>>(() => new Set());
  useEffect(() => setStarted(startedGoalsOverride ? new Set(startedGoalsOverride) : readStartedGoals()), [startedGoalsOverride]);

  const now = Date.now();
  const { cards, all } = useMemo(
    () => buildComingUp({ tasks, routines, goals, liveWorkspaceIds, now }),
    // `now` moves every render; the section refreshes with its reads, never on a clock (spec: no live ticking).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, routines, goals, liveWorkspaceIds],
  );
  const goalsLeft = useMemo(
    () => availableGoalIds(GOAL_INTENTS.map((g) => g.id), started).map((id) => GOAL_INTENTS.find((g) => g.id === id)!),
    [started],
  );
  const { state, partial } = comingUpState([reads.tasks, reads.routines, reads.goals], all.length, goalsLeft.length);
  const slots: Slot[] = [
    ...cards.map((item): Slot => ({ kind: 'item', item })),
    ...goalsLeft.slice(0, goalSlots(cards.length)).map((goal): Slot => ({ kind: 'goal', goal })),
  ];

  const locale = i18n.language || undefined;
  const clock = useCallback(
    (at: number) => new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at),
    [locale],
  );
  const routineWhen = (at: number) => `${new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(at)} ${clock(at)}`;
  const chipLabel = (chip: ComingUpChip): string => {
    switch (chip.kind) {
      case 'needsYou':
        return t('home.comingUp.chip.needsYou');
      case 'overdue':
        return t('home.comingUp.chip.overdue');
      case 'today':
        return t('home.comingUp.chip.today', { time: clock(chip.at) });
      case 'tomorrow':
        return t('home.comingUp.chip.tomorrow', { time: clock(chip.at) });
      case 'inDays':
        return t('home.comingUp.chip.inDays', { count: chip.days });
      case 'routine':
        return t('home.comingUp.chip.routine', { when: routineWhen(chip.at) });
      case 'suggestedGoal':
        return t('home.comingUp.chip.suggestedGoal');
    }
  };

  const open = (item: ComingUpItem) => {
    logPageClickBi(
      { page_name: HOME3_PAGE, button_name: `coming_up_open_${item.kind}`, ...(item.target.type === 'task' ? { task_id: item.target.id } : {}) },
      { componentName: 'coming-up' },
    );
    onOpen(item);
  };

  const startGoal = (goal: GoalIntent) => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_start_goal' }, { componentName: `coming-up-goal-${goal.id}` });
    // Same start as the goal picker's card: the seed the user appears to type, the band's brief beside it.
    openSideChat({ kind: 'new', message: t(goal.seedKey), context: goalIntentContext(goal) });
    const next = new Set(started).add(goal.id);
    writeStartedGoals(next);
    setStarted(next);
  };

  const askMaestro = () => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_ask_maestro' });
    openSideChat({ kind: 'new', message: t('home.comingUp.allClear.seed'), context: ASK_MAESTRO_CONTEXT });
  };

  const retry = () => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_retry' });
    onRetry();
  };

  const who = (item: ComingUpItem): { mark: ReactNode; text: string } => {
    const space = item.workspaceId ? spaceNames[item.workspaceId] ?? '' : '';
    const agent = item.agentId ? agentNames[item.agentId] ?? '' : item.maestro ? maestroName : '';
    const mark = item.agentId ? <AgentDot id={item.agentId} size={16} /> : item.maestro ? <MaestroMark size={16} /> : null;
    return { mark: agent ? mark : null, text: [agent, space].filter(Boolean).join(' | ') };
  };

  const slotView = (slot: Slot) => {
    if (slot.kind === 'goal') {
      const goal = slot.goal;
      const chip = <Badge variant={CHIP_VARIANT.suggestedGoal} size="tiny" label={chipLabel({ kind: 'suggestedGoal' })} />;
      return {
        key: `goal:${goal.id}`,
        chip,
        rowChip: chip,
        title: t(goal.titleKey),
        rowTitle: t('home.comingUp.goalTitle', { title: t(goal.titleKey) }),
        sub: { mark: null, text: t(goal.bandNameKey) },
        action: t('home.comingUp.startGoal'),
        run: () => startGoal(goal),
      };
    }
    const item = slot.item;
    const chip = comingUpChip(item, now);
    const cardChip = (
      <Badge
        variant={CHIP_VARIANT[chip.kind]}
        size="tiny"
        label={chipLabel(chip)}
        startIcon={chip.kind === 'routine' ? <Repeat size={12} aria-hidden /> : undefined}
      />
    );
    // A row says "Routine" in its title (Figma 21639:675067), so its chip keeps only the next run.
    return {
      key: item.id,
      chip: cardChip,
      rowChip:
        chip.kind === 'routine' ? <Badge variant={CHIP_VARIANT.routine} size="tiny" label={routineWhen(chip.at)} /> : cardChip,
      title: item.title,
      rowTitle: chip.kind === 'routine' ? t('home.comingUp.routineTitle', { title: item.title }) : item.title,
      sub: who(item),
      action: t(comingUpAction(item) === 'approve' ? 'home.comingUp.approve' : 'home.comingUp.view'),
      run: () => open(item),
    };
  };

  const subLine = (sub: { mark: ReactNode; text: string }) =>
    sub.text ? (
      <span className={styles.sub}>
        {sub.mark}
        <Typography variant="tiny-medium" color="secondary" component="span" className={styles.subText}>
          {sub.text}
        </Typography>
      </span>
    ) : null;

  /** The desktop and modal row: title and who on the left, then the chip, then the button. */
  const row = (slot: Slot, onRun?: () => void) => {
    const v = slotView(slot);
    const run = () => {
      onRun?.();
      v.run();
    };
    return (
      <li key={v.key} className={styles.row} onClick={fromCardBackground(run)}>
        <span className={styles.rowBody}>
          <Typography variant="small-medium" color="primary" component="span" className={styles.title}>
            {v.rowTitle}
          </Typography>
          {subLine(v.sub)}
        </span>
        {slot.kind === 'item' && v.rowChip}
        <Button variant="outlined" size="tiny" onClick={run}>
          {v.action}
        </Button>
      </li>
    );
  };

  /** The phone card: chip, title and who stacked on the left, the button on the right. */
  const card = (slot: Slot) => {
    const v = slotView(slot);
    return (
      <Card key={v.key} variant="sunken" size="sm" className={styles.card} onClick={fromCardBackground(v.run)}>
        <span className={styles.cardBody}>
          {v.chip}
          <Typography variant="small-medium" color="primary" component="span" className={styles.title}>
            {v.title}
          </Typography>
          {subLine(v.sub)}
        </span>
        <Button variant="outlined" size="tiny" onClick={v.run}>
          {v.action}
        </Button>
      </Card>
    );
  };

  const body = (() => {
    if (state === 'loading') {
      return narrow ? (
        <div className={styles.cards} aria-busy>
          {[0, 1, 2].map((i) => (
            <Card key={i} variant="sunken" size="sm" className={`${styles.card} ${styles.placeholderCard}`}>
              <span className={styles.placeholderLine} />
              <span className={`${styles.placeholderLine} ${styles.placeholderWide}`} />
            </Card>
          ))}
        </div>
      ) : (
        <ul className={styles.rows} aria-busy>
          {[0, 1, 2].map((i) => (
            <li key={i} className={styles.row}>
              <span className={styles.rowBody}>
                <span className={`${styles.placeholderLine} ${styles.placeholderWide}`} />
                <span className={styles.placeholderLine} />
              </span>
            </li>
          ))}
        </ul>
      );
    }
    if (state === 'failed') {
      return (
        <div className={styles.message} role="alert">
          <Typography variant="p2" color="secondary" component="p">
            {t('home.comingUp.failed')}
          </Typography>
          <Button variant="outlined" size="tiny" onClick={retry}>
            {t('home.comingUp.retry')}
          </Button>
        </div>
      );
    }
    if (state === 'allClear') {
      return (
        <div className={styles.message}>
          <Typography variant="h4" color="primary" component="p">
            {t('home.comingUp.allClear.title')}
          </Typography>
          <Typography variant="p2" color="secondary" component="p">
            {t('home.comingUp.allClear.body', { maestroAgentName: maestroName })}
          </Typography>
          <Button variant="outlined" size="tiny" onClick={askMaestro}>
            {t('home.comingUp.allClear.cta', { maestroAgentName: maestroName })}
          </Button>
        </div>
      );
    }
    const list = narrow ? (
      <ScrollArea axis="horizontal" bar="hidden" className={styles.cardsScroller}>
        <div className={styles.cards}>{slots.map(card)}</div>
      </ScrollArea>
    ) : (
      <ul className={styles.rows}>{slots.map((slot) => row(slot))}</ul>
    );
    return (
      <>
        {state === 'pickGoal' && (
          <div className={styles.intro}>
            <Typography variant="h4" color="primary" component="p">
              {t('home.comingUp.pickGoal.title')}
            </Typography>
            <Typography variant="p2" color="secondary" component="p">
              {t('home.comingUp.pickGoal.body')}
            </Typography>
          </div>
        )}
        {list}
      </>
    );
  })();

  return (
    <section className={`${styles.root}${narrow ? ` ${styles.narrow}` : ''}`} aria-labelledby="home3-coming-up-title" data-testid="home3-coming-up">
      <div className={styles.head}>
        <Typography id="home3-coming-up-title" variant="display-05" color="primary" component="h2">
          {t('home.comingUp.title')}
        </Typography>
        {showsShowAll(state) && (
          // Chevron-right: "Show all" opens the Coming-up modal IN PLACE, so it takes the in-context
          // disclosure glyph rather than the ↗ that means "navigates away" (Figma 21882-133470). This now
          // matches the native Coming-up headers (`HomeComingUpSection.swift` / Android `ComingUpSection.kt`),
          // closing the earlier web-first ↗ divergence.
          <TextButton
            size="small"
            endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />}
            onClick={() => {
              logPageClickBi({ page_name: HOME3_PAGE, button_name: 'coming_up_show_all' });
              setShowAll(true);
            }}
          >
            {t('home.comingUp.showAll')}
          </TextButton>
        )}
      </div>
      {body}
      {partial && (
        <div className={styles.partial}>
          <Typography variant="p3" color="secondary" component="span">
            {t('home.comingUp.partial')}
          </Typography>
          <TextButton size="tiny" underline onClick={retry}>
            {t('home.comingUp.retry')}
          </TextButton>
        </div>
      )}
      <Modal
        open={showAll}
        onClose={() => setShowAll(false)}
        title={t('home.comingUp.title')}
        size="small"
        maxHeightPx={650}
        scrollBody
        presentation={narrow ? 'sheet' : 'centered'}
        footer={
          <Button variant="outlined" onClick={() => setShowAll(false)}>
            {t('home.comingUp.modal.close')}
          </Button>
        }
      >
        <ul className={`${styles.rows} ${styles.modalRows}`}>
          {all.map((item) => row({ kind: 'item', item }, () => setShowAll(false)))}
        </ul>
      </Modal>
    </section>
  );
}
